import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, QueryList, ViewChildren, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import '@maersk-global/mds-components-core/mc-icon';
import { CategoryCardInput } from '../../models/vsc-scorecard.model';
import { VscKpiTrendCardComponent } from '../vsc-kpi-trend-card/vsc-kpi-trend-card.component';

// Maps category id to its MDS icon name
const CATEGORY_ICONS: Record<number, string> = {
  1: 'clock',
  2: 'clipboard-check',
  3: 'monitor',
  4: 'file-ribbon',
};

type ScoreBand = 'poor' | 'average' | 'good' | 'excellent';

@Component({
  selector: 'app-vsc-category-card',
  imports: [CommonModule, VscKpiTrendCardComponent],
  templateUrl: './vsc-category-card.component.html',
  styleUrl: './vsc-category-card.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VscCategoryCardComponent implements OnInit {
  @Input({ required: true }) data!: CategoryCardInput;
  @ViewChildren(VscKpiTrendCardComponent) private readonly kpiCards!: QueryList<VscKpiTrendCardComponent>;

  readonly isExpanded = signal(false);

  ngOnInit(): void {
    this.isExpanded.set(this.data.expanded ?? false);
  }

  toggle(): void {
    this.isExpanded.update((v) => !v);
  }

  expandAllKpis(): void {
    this.kpiCards.forEach((k) => k.isExpanded.set(true));
  }

  collapseAllKpis(): void {
    this.kpiCards.forEach((k) => k.isExpanded.set(false));
  }

  /** Empty while the category is collapsed — the KPI cards are not in the DOM then. */
  kpiExpansionState(): boolean[] {
    return this.kpiCards?.map((k) => k.isExpanded()) ?? [];
  }

  restoreKpiExpansion(state: boolean[]): void {
    if (!state.length) return;
    // The KPI cards only render once the category re-expands, so wait a tick for the QueryList to refill.
    setTimeout(() => this.kpiCards?.forEach((k, i) => k.isExpanded.set(state[i] ?? false)), 0);
  }

  get categoryIcon(): string {
    return CATEGORY_ICONS[this.data.categoryId] ?? 'check-circle';
  }

  private get isShopView(): boolean {
    return this.data.isShopView ?? false;
  }

  get displayScore(): number {
    return this.isShopView ? this.data.outOfFiveScore : this.data.outOfTotalScore;
  }

  get displayMax(): number {
    return this.isShopView ? (this.data.maxOutOfFiveScore ?? 5) : this.data.maxOutOfTotalScore;
  }

  get progressWidth(): string {
    return `${(this.displayScore / this.displayMax) * 100}%`;
  }

  // Colour band is always driven by the 0–5 score so categories and KPIs use the same palette
  private get scoreBand(): ScoreBand {
    const s = this.data.outOfFiveScore;
    if (s < 2) return 'poor';
    if (s < 3) return 'average';
    if (s <= 4.5) return 'good';
    return 'excellent';
  }

  get scoreClass(): string {
    return `score--${this.scoreBand}`;
  }

  // Separate bg class so header-area gets tinted background while card gets only a border
  get scoreBgClass(): string {
    return `${this.scoreClass}-bg`;
  }

  get visibleKpis() {
    return this.data.kpis.filter((k) => k.kpiWeight !== 0).map((k) => ({ ...k, isShopView: this.data.isShopView ?? false }));
  }
}
